import { createFFmpeg, FFmpeg, ProgressCallback } from '@ffmpeg/ffmpeg'
import { MediaType } from '@/utils/media-types'

export { ProgressCallback }

export interface Metadata {
  title?: string
  date?: Date
  copyright?: string
  artist?: string
  album_artist?: string
  author?: string
  composer?: string
  album?: string
  comment?: string
  genre?: string
  lyrics?: string
}

interface SourceFile {
  blob: Blob
  mediaType: MediaType
  outputFileName: string
  metadata?: Metadata
}

export interface OutputFile {
  blob: Blob
  name: string
}

// eslint-disable-next-line
export type CompleteCallback = (file: OutputFile) => any

/**
 * ConvertToMp4
 *
 * Converts video files into the mp4 format.
 *
 * Source files can be provided in any video container format supported by ffmpeg.
 *
 * Video streams are copied from source files into destination files. They are not re-encoded.
 * Therefore video streams in source files must be encoded using codecs supported by the mp4
 * container format. In practice this means h264 or h265.
 *
 * Audio streams in source files are re-encoded to acc and can therefore be encoded using
 * any codec supported by ffmpeg.
 *
 * ConvertToMp4 uses ffmpeg WASM internally. ffmpeg WASM requires web browsers that support
 * SharedArrayBuffer.
 */
export default class FFmpegUtility {
  private static instance: FFmpegUtility

  private corePath: string
  private ffmpeg?: FFmpeg
  private fileQueue: SourceFile[] = []
  private ready = false
  private processing = false
  private progress: ProgressCallback = () => { /* Do nothing by default */ }
  private complete: CompleteCallback = () => { /* Do nothing by default */ }
  private outputFileIndex = 0
  private ffmpegLoaded: Promise<void> | undefined

  constructor (corePath: string) {
    this.corePath = corePath
  }

  public static getInstance (): FFmpegUtility {
    if (!FFmpegUtility.instance) {
      FFmpegUtility.instance = new FFmpegUtility('ffmpeg-0-10-0/ffmpeg-core.js')
      FFmpegUtility.instance.ffmpegLoaded = FFmpegUtility.instance.load()
    }
    return FFmpegUtility.instance
  }

  /** Promise to await on to ensure that ffmpeg has loaded before use */
  public get ffmpegLoadedPromise (): Promise<void> | undefined {
    return this.ffmpegLoaded
  }
  
  /**
   * Load internal components used by ConvertToMp4.
   *
   * Call this method as early as possible.
   */
  public async load (log = false) {
    this.ffmpeg = createFFmpeg({
      log: log,
      corePath: this.corePath
    })

    // When `true` ffmpeg provides detailed logging to the console.
    // Very useful, but probably best to disable in productiom.
    this.ffmpeg.setLogging(log)
    await this.ffmpeg.load()
    this.ffmpeg.setProgress(this.progress)
    this.ready = true
    this.processQueue()
  }

  public exit (): boolean {
    if (this.ready) {
      this.ffmpeg?.exit()
    }
    return this.ready
  }

  /**
   * Adds a progress handler to the underlying ffmpeg instance
   */
  public setProgress (progress: ProgressCallback): void {
    this.progress = progress
    if (this.ffmpeg !== undefined) {
      // The ffmpeg `setProgress` callback is not super useful.
      // It returns a ratio value 0 when `run` is started (good).
      // Then a whole bunch of NaN values during execution (bad).
      // Then a 1 when the `run` finishes execution (good).
      // So it can only be used to let us know that `run` has started, is in progress and has finished.
      // It doesn't give us actual progress.
      // It's possible that this is due to the actual command that we're executing.
      // (ie. shifting the existing video stream into a new container).
      // Maybe it provides more meaningful progress values when doing actual video encoding???
      this.ffmpeg.setProgress(this.progress)
    }
  }

  /**
   * Adds a complete handler.
   *
   * The handler is called each time a file conversion completes.
   */
  public setComplete (complete: CompleteCallback): void {
    this.complete = complete
  }

  public async addAudioToMp4 (audioFileName: string, videoBlob: Uint8Array, audioData: Uint8Array, audioStartTime: number, audioEndTime: number, fps: number, isAudioTrimmed: boolean): Promise<Uint8Array> {
    const fileExtension = audioFileName.split('.').pop()
    if (!this.ffmpeg) {
      throw new Error('ffmpeg is not loaded')
    }

    if (!this.ready) {
      await this.ffmpegLoadedPromise
    }

    this.outputFileIndex++
    const rawVideo = `raw${this.outputFileIndex}.h264`
    const finalVideoName = `output${this.outputFileIndex}.mp4`
    const videoName = 'video.mp4'
    const audioName = `audio.${fileExtension}`
    const trimmedAudio = `audioTrimmed${this.outputFileIndex}.${fileExtension}`
    let outputAudio = audioName
    this.ffmpeg.FS(
      'writeFile',
      videoName,
      videoBlob
    )
    this.ffmpeg.FS(
      'writeFile',
      audioName,
      audioData
    ) 
    // remove the video from the container
    await this.ffmpeg.run('-y', '-i', videoName, '-c', 'copy', '-f', 'h264', rawVideo)
    // recontainerize video with new fps and audio

    if (isAudioTrimmed) {
      // trim audio to desired length
      await this.ffmpeg.run('-ss', audioStartTime.toString(), '-i', audioName, '-t', (audioEndTime - audioStartTime).toString(), '-c', 'copy', trimmedAudio)
      outputAudio = trimmedAudio
    }

    await this.ffmpeg.run('-r', fps.toString(), '-i', rawVideo, '-i', outputAudio, '-map', '0:v', '-map', '1:a', '-c:v', 'copy', '-shortest', finalVideoName)
    const outputFile = this.ffmpeg.FS('readFile', finalVideoName)
    this.ffmpeg.FS('unlink', rawVideo)
    this.ffmpeg.FS('unlink', finalVideoName)
    this.ffmpeg.FS('unlink', videoName)
    this.ffmpeg.FS('unlink', audioName)

    return outputFile
  }

  /**
   * Adds a file blob to be converted to mp4.
   *
   * Files are processed serially in FIFO order.
   */
  public addFile (blob: Blob, mediaType: MediaType, outputFileName: string, metadata?: Metadata): void {
    this.fileQueue.push({ blob, mediaType, outputFileName, metadata })
    this.processQueue()
  }

  private async processQueue () {
    if (this.processing) {
      return
    }
    this.processing = true
    while (this.ready && this.fileQueue.length > 0) {
      await this.convertToMp4(this.fileQueue.shift())
    }
    this.processing = false
  }

  private async convertToMp4 (file?: SourceFile) {
    if (this.ffmpeg !== undefined && file !== undefined) {
      const { blob, mediaType, outputFileName, metadata }: SourceFile = file

      const virtualSourceFileName = 'input.' + mediaType.fileExtension
      const virtualOutputFileName = 'output.mp4'

      // Create an ArrayBuffer from the Blob
      const sourceBuffer = await blob.arrayBuffer()

      // Write the input file to the ffmpeg file system
      this.ffmpeg.FS(
        'writeFile',
        virtualSourceFileName,
        new Uint8Array(sourceBuffer, 0, sourceBuffer.byteLength)
      )

      // Run the ffmpeg command to convert file into an MP4.
      // So long as the video codec used is h264 all we have to do is create an mp4 container
      // transcode the audio to AAC, and move the existing video stream into it.
      // The `-c:v copy` arguments do this.

      // FWIW, I don't think any browsers support encoding audio in mp3 or any other format supported
      // by the mp4 container spec (unless the browser can natively create mp4 containers of course).
      // If would be nice if we could copy the audio stream over too because this would make the process
      // a bit faster. But we have to transcode it.

      // However, it takes between 1 - 2 seconds to transcode 1 minute of audio so it's not a deal breaker.

      await this.ffmpeg.run(
        '-i',
        virtualSourceFileName,
        ...this.metadataToArgList(metadata),
        '-c:v',
        'copy',
        virtualOutputFileName
      )

      // Read the MP4 file back from the ffmpeg file system
      const output: Uint8Array = this.ffmpeg.FS('readFile', virtualOutputFileName)

      // Delete the file from the ffmpeg file system
      this.ffmpeg.FS('unlink', virtualOutputFileName)

      // Create a Blob from output data
      const outputBlob = new Blob([output.buffer], { type: 'video/mp4' })

      this.complete({ blob: outputBlob, name: outputFileName })
    }
  }

  private metadataToArgList (metadata?: Metadata): string[] {
    const args: string[] = []
    let k: keyof Metadata
    if (metadata !== undefined) {
      for (k in metadata) {
        args.push('-metadata')
        let argVal: string
        if (metadata[k] instanceof Date) {
          const dt = <Date>metadata[k]
          argVal = dt.toISOString()
        } else {
          argVal = <string>metadata[k]
        }
        args.push(k + '=' + argVal)
      }
    }
    return args
  }

  /**
   * Get video codec for a video file
   * @param blob 
   * @param extension prefix with a full stop e.g. .mp4
   * @returns codec name
   */
  public async getVideoCodec (blob: Blob, extension: string): Promise<string> {
    if (!this.ffmpeg) {
      throw new Error('ffmpeg is not loaded')
    }

    if (!this.ready) {
      await this.ffmpegLoadedPromise
    }

    // Filter the console output for the video codec
    let codec = ''
    this.ffmpeg.setLogger(({ message }) => {
      if (message.includes('Stream')) {
        codec = message.split('Video: ')[1].split(',')[0]
      }
    })

    const virtualSourceFileName = 'input' + extension

    // Create an ArrayBuffer from the Blob
    const sourceBuffer = await blob.arrayBuffer()

    // Write the input file to the ffmpeg file system
    this.ffmpeg.FS(
      'writeFile',
      virtualSourceFileName,
      new Uint8Array(sourceBuffer, 0, sourceBuffer.byteLength)
    )

    await this.ffmpeg.run('-i', virtualSourceFileName, '-hide_banner')

    return codec
  }
}